import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useContent } from '@backstage/core-components';
import {
  SearchContextProvider,
  SearchBar,
  SearchResult,
  SearchResultListItemExtensions,
  SearchResultPager,
} from '@backstage/plugin-search-react';
import { useSearchModal } from '@backstage/plugin-search';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { EmptyState } from '@ciandtspike.io/plugin-ui';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Box,
  Button,
  IconButton,
  SvgIcon,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import { TbBuildingStore } from 'react-icons/tb';
import { useTheme } from '@material-ui/core/styles';
import { toMarketplacePath } from '@ciandtspike.io/plugin-ciandtspike-common';
import { MarketplaceSearchResultListItem } from '../MarketplaceSearchResultListItem';
import { ProvisionedResourcesSearchResultListItem } from '../ProvisionedResourcesSearchResultListItem';
import { spikeSearchModalTranslationRef } from './translation';
import { useStyles } from './SpikeSearchModal.style';

function SpikeSearchModalContent({ toggleModal }: { toggleModal: () => void }) {
  const classes = useStyles();
  const { t } = useTranslationRef(spikeSearchModalTranslationRef);
  const navigate = useNavigate();
  const { transitions } = useTheme();
  const { focusContent } = useContent();
  const searchBarRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchBarRef?.current?.focus();
  });

  const handleResultClick = useCallback(() => {
    setTimeout(focusContent, transitions.duration.leavingScreen);
    toggleModal();
  }, [focusContent, transitions, toggleModal]);

  const handleSubmit = useCallback(() => {
    const query = searchBarRef.current?.value ?? '';
    navigate(`/search?query=${query}`);
    handleResultClick();
  }, [navigate, handleResultClick]);

  return (
    <>
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <SearchBar
            className={classes.input}
            inputProps={{ ref: searchBarRef }}
            placeholder={t('modal.searchPlaceholder')}
            onSubmit={handleSubmit}
          />
          <IconButton aria-label={t('modal.closeButton')} onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="row-reverse" justifyContent="flex-start" alignItems="center">
          <Grid item>
            <Button
              className={classes.button}
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleSubmit}
              disableRipple
            >
              View full results
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <SearchResult
          noResultsComponent={<SpikeSearchEmptyState toggleModal={toggleModal} />}
        >
          {({ results }) => {
            const visible = results.filter(r => {
              if (r.type !== 'software-catalog') return true;
              const doc = r.document as any;
              return !doc.provisioned_resource_ref &&
                !doc.marketplace_product_id &&
                doc.kind !== 'Template' &&
                doc.kind !== 'Location';
            });
            if (visible.length === 0) return <SpikeSearchEmptyState toggleModal={toggleModal} />;
            return (
              <SearchResultListItemExtensions
                results={visible}
                onClick={handleResultClick}
                onKeyDown={handleResultClick}
              >
                <MarketplaceSearchResultListItem />
                <ProvisionedResourcesSearchResultListItem />
                <CatalogSearchResultListItem />
              </SearchResultListItemExtensions>
            );
          }}
        </SearchResult>
      </DialogContent>
      <DialogActions className={classes.dialogActionsContainer}>
        <Grid container direction="row">
          <Grid item xs={12}>
            <SearchResultPager />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}

function SpikeSearchEmptyState({ toggleModal }: { toggleModal: () => void }) {
  const classes = useStyles();
  const { t } = useTranslationRef(spikeSearchModalTranslationRef);
  const navigate = useNavigate();

  return (
    <div className={classes.emptyStateWrapper}>
      <EmptyState
        icon={<SvgIcon><TbBuildingStore size={24} /></SvgIcon>}
        title={t('modal.empty.title')}
        description={t('modal.empty.description')}
        cta={{
          label: t('modal.empty.cta'),
          onClick: () => {
            toggleModal();
            navigate(toMarketplacePath.root());
          },
        }}
      />
    </div>
  );
}

export function SpikeSearchModal() {
  const classes = useStyles();
  const { state, toggleModal } = useSearchModal();
  const isOpen = state.open && !state.hidden;

  return (
    <Dialog
      classes={{ paperFullWidth: classes.paperFullWidth }}
      onClose={toggleModal}
      aria-label="Search Modal"
      aria-modal="true"
      fullWidth
      maxWidth="lg"
      open={isOpen}
    >
      {isOpen && (
        <SearchContextProvider inheritParentContextIfAvailable>
          <SpikeSearchModalContent toggleModal={toggleModal} />
        </SearchContextProvider>
      )}
    </Dialog>
  );
}
